import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Ruler, RefreshCw, ChevronRight, CheckCircle2, Clock, Loader2, RotateCcw } from 'lucide-react';

// CFT sheets an admin assigned to this worker (approved, read-only). The worker
// fills in the Tally details, then marks the sheet complete.
const when = d => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');

export default function MyCftSheetsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/cft-assigned'); setRows(r.data || []); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pending = rows.filter(r => !r.completed_at);
  const completed = rows.filter(r => r.completed_at);
  const shown = tab === 'pending' ? pending : tab === 'completed' ? completed : rows;

  const setDone = async (s, done) => {
    if (done && !s.entered_in_tally) {
      toast.error('Fill in the Tally details first');
      return navigate(`/worker/my-cft/${s.id}`);
    }
    if (!done && !window.confirm(`Mark ${s.sheet_no} as pending again?`)) return;
    setBusy(s.id);
    try { const r = await api.post(`/cft-assigned/${s.id}/complete`, { done }); toast.success(r.data.message); await load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Could not update'); }
    finally { setBusy(null); }
  };

  const Card = ({ k, label, value, icon: Icon, cls }) => (
    <button onClick={() => setTab(k)} className={`text-left rounded-xl border p-3 ${cls} ${tab === k ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}>
      <div className="flex items-center justify-between"><span className="text-2xl font-bold">{value}</span>{Icon && <Icon className="w-5 h-5 opacity-60" />}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </button>
  );

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-800 flex items-center gap-2"><Ruler className="w-6 h-6 text-blue-600" /> My CFT Sheets</h1>
          <p className="text-sm text-gray-500">Fill in the Tally details, then mark each sheet complete</p>
        </div>
        <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50"><RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} /></button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card k="pending" label="Pending" value={pending.length} icon={Clock} cls="bg-amber-50 border-amber-200 text-amber-800" />
        <Card k="completed" label="Completed" value={completed.length} icon={CheckCircle2} cls="bg-green-50 border-green-200 text-green-800" />
        <Card k="all" label="Total Assigned" value={rows.length} icon={Ruler} cls="bg-gray-50 border-gray-200 text-gray-800" />
      </div>

      {loading ? <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        : !shown.length ? (
          <div className="bg-white rounded-xl border border-dashed border-gray-200 p-10 text-center text-gray-400 text-sm">
            {!rows.length ? 'No CFT sheet is assigned to you' : tab === 'pending' ? 'Nothing pending — all done 🎉' : tab === 'completed' ? 'No sheet completed yet' : 'No sheets'}
          </div>
        ) : (
          <div className="grid gap-3">
            {shown.map(s => (
              <div key={s.id} className={`bg-white rounded-xl border p-4 flex flex-wrap items-center gap-3 ${s.completed_at ? 'border-green-200' : 'border-gray-200'}`}>
                <button onClick={() => navigate(`/worker/my-cft/${s.id}`)} className="flex-1 min-w-[14rem] text-left flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-bold text-blue-700">{s.sheet_no}</span>
                      {s.completed_at
                        ? <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-green-100 text-green-700">✓ Completed</span>
                        : <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800">Pending</span>}
                      {s.entered_in_tally
                        ? <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${s.entered_in_tally === 'yes' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>Tally: {s.entered_in_tally === 'yes' ? 'Yes' : 'No'}{s.stock_journal_no ? ` · ${s.stock_journal_no}` : ''}</span>
                        : <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-gray-100 text-gray-500">Details not filled</span>}
                    </div>
                    <div className="text-sm text-gray-700 mt-0.5">{s.project_name || s.project_code || '—'}{s.item_name ? ` · ${s.item_name}` : ''}</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                      {s.wood_type || 'Wood —'} · Net {Number(s.net_difference || 0).toFixed(4)} CFT · Assigned {when(s.assigned_at)}{s.completed_at ? ` · Completed ${when(s.completed_at)}` : ''}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </button>
                {s.completed_at ? (
                  <button onClick={() => setDone(s, false)} disabled={busy === s.id}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                    {busy === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Undo
                  </button>
                ) : (
                  <button onClick={() => setDone(s, true)} disabled={busy === s.id}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-xs font-semibold disabled:opacity-50">
                    {busy === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Mark as Complete
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
